import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDispatch } from 'react-redux';
import { getAuthCookieInfo } from 'utils/functions/authentication';
import { createDiscount } from '../../actions';
import discountsStrings from '../../strings';
import { buildCreateOfferPayload } from '../../utils/discountHelpers';
import type { DiscountProduct } from '../../types/discount.types';

const today = new Date().toISOString().slice(0, 10);

const formSchema = z
  .object({
    startDate: z.string().min(1, discountsStrings.validationRequired),
    endDate: z.string().min(1, discountsStrings.validationRequired),
    discountValue: z
      .string()
      .min(1, discountsStrings.validationRequired)
      .refine(v => Number(v) >= 0, discountsStrings.validationNegativeDiscount),
    discountType: z.enum(['percent', 'amount']),
    originalPrice: z.number(),
  })
  .superRefine((data, ctx) => {
    if (data.startDate && data.startDate < today) {
      ctx.addIssue({
        code: 'custom',
        message: discountsStrings.validationStartDateBeforeToday,
        path: ['startDate'],
      });
    }
    if (data.startDate && data.endDate && data.endDate <= data.startDate) {
      ctx.addIssue({
        code: 'custom',
        message: discountsStrings.validationEndDateAfterStart,
        path: ['endDate'],
      });
    }
    const value = Number(data.discountValue);
    if (data.discountType === 'percent' && value > 100) {
      ctx.addIssue({
        code: 'custom',
        message: discountsStrings.validationPercentMax,
        path: ['discountValue'],
      });
    }
    if (data.discountType === 'amount' && value > data.originalPrice) {
      ctx.addIssue({
        code: 'custom',
        message: discountsStrings.validationAmountExceedsPrice,
        path: ['discountValue'],
      });
    }
  });

export type CreateBundleFormValues = z.infer<typeof formSchema>;

interface UseCreateBundleFormOptions {
  isOpen: boolean;
  selectedBundle: DiscountProduct | null;
}

export function useCreateBundleForm({ isOpen, selectedBundle }: UseCreateBundleFormOptions) {
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      startDate: '',
      endDate: '',
      discountValue: '',
      discountType: 'percent',
      originalPrice: 0,
    },
  });

  const watchedStartDate = watch('startDate');
  const watchedDiscountType = watch('discountType');

  useEffect(() => {
    if (isOpen) return;
    reset({
      startDate: '',
      endDate: '',
      discountValue: '',
      discountType: 'percent',
      originalPrice: 0,
    });
  }, [isOpen, reset]);

  useEffect(() => {
    setValue('originalPrice', selectedBundle?.originalPrice ?? 0);
  }, [selectedBundle, setValue]);

  const handleFormSubmit = handleSubmit(data => {
    if (!selectedBundle) return;
    const { pharmacyKey } = getAuthCookieInfo();
    dispatch(
      createDiscount(
        buildCreateOfferPayload({
          offerLines: [
            {
              productKey: selectedBundle.productKey,
              productShapeId: selectedBundle.productShapeId ?? 0,
              productName: selectedBundle.productName,
              productShapeName: '',
              productImageUrl: selectedBundle.productImageUrl || '',
              originalPrice: selectedBundle.originalPrice,
              existingOfferStatus: null,
              existingOfferStatusLabel: null,
              discountValue: data.discountValue,
              discountType: data.discountType,
            },
          ],
          startDate: data.startDate,
          endDate: data.endDate,
          providerKey: pharmacyKey,
        }),
      ),
    );
  });

  return {
    register,
    errors,
    watchedStartDate,
    watchedDiscountType,
    handleFormSubmit,
  };
}
